import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FacebookProvider } from "react-facebook";
import { Button, Collapse } from "reactstrap";
import { toast } from "react-toastify";
import { useMeCore } from "../../../../cores/me";
import { useIntl } from "react-intl";
import { useWhatsappCore } from "../../../../cores/whatsapp";
import { useCompanyCore } from "../../../../cores/company";
import {
  WHATSAPP_FACEBOOK_APP_ID,
  WhatsAppConnectionMode,
} from "../../../../cores/whatsapp/config";
import { launchWhatsAppSignup } from "../../../../cores/whatsapp/services/whatsapp-auth0-config";
import { UpgradePlanButton } from "../../plan/UpgradePlanButton";

export default function WhatsappAuthButton() {
  const intl = useIntl();
  const { companyRole, companyPlan, company } = useMeCore();
  const { get, settings, unbind, completeAuth0Step } = useWhatsappCore();
  const { getCompanyIntegration } = useCompanyCore();
  const [showCoexistenceHelp, setShowCoexistenceHelp] = useState(false);

  useEffect(() => {
    get(company!.id);
  }, [company, get]);

  const planAllowsWhatsapp = useMemo(
    () =>
      (companyRole?.owner || companyRole?.admin) &&
      companyPlan?.rules.allowWhatsappChat,
    [companyPlan, companyRole]
  );
  const isActive = useMemo((): boolean => !_.isEmpty(settings), [settings]);

  const handleUnbind = useCallback(async () => {
    unbind(company?.id!, () => {
      getCompanyIntegration(company?.id!);
    });
  }, [company, getCompanyIntegration, unbind]);

  const handleConnect = useCallback(
    (mode: WhatsAppConnectionMode) => {
      try {
        launchWhatsAppSignup(mode, ({ authResponse }: any) => {
          if (authResponse?.code) {
            completeAuth0Step(company?.id!, authResponse.code, mode);
            getCompanyIntegration(company?.id!);
          } else {
            console.log("User cancelled login or did not fully authorize.");
          }
        });
      } catch (error) {
        console.error("[WhatsApp connect]", error);
        toast.error(intl.formatMessage({ id: "app.whatsappAuth.authError" }));
      }
    },
    [company, completeAuth0Step, getCompanyIntegration, intl]
  );

  const connectPanel = (
    <div className="d-flex flex-column gap-2 w-100">
      <Button
        className="w-100 mx-auto"
        color="primary"
        onClick={() => handleConnect("coexistence")}
        disabled={!WHATSAPP_FACEBOOK_APP_ID}
      >
        {intl.formatMessage({ id: "app.whatsappConnect.existingBusiness" })}
      </Button>
      <Button
        className="w-100 mx-auto"
        color="outline-primary"
        onClick={() => handleConnect("api_only")}
        disabled={!WHATSAPP_FACEBOOK_APP_ID}
      >
        {intl.formatMessage({ id: "app.whatsappConnect.newNumber" })}
      </Button>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-start"
        onClick={() => setShowCoexistenceHelp((open) => !open)}
      >
        {intl.formatMessage({ id: "app.whatsappConnect.helpToggle" })}
      </button>
      <Collapse isOpen={showCoexistenceHelp}>
        <div className="small text-muted border rounded p-2 bg-light">
          <p className="mb-1">
            {intl.formatMessage({ id: "app.whatsappConnect.helpLine1" })}
          </p>
          <p className="mb-1">
            {intl.formatMessage({ id: "app.whatsappConnect.helpLine2" })}
          </p>
          <p className="mb-0">
            {intl.formatMessage({ id: "app.whatsappConnect.helpLine3" })}
          </p>
        </div>
      </Collapse>
    </div>
  );

  return !settings?.token ? (
    <div className="w-100 px-2">
      {planAllowsWhatsapp ? (
        WHATSAPP_FACEBOOK_APP_ID ? (
          <FacebookProvider version="v22.0" appId={WHATSAPP_FACEBOOK_APP_ID}>
            {connectPanel}
          </FacebookProvider>
        ) : (
          connectPanel
        )
      ) : (
        <UpgradePlanButton expanded />
      )}
    </div>
  ) : (
    <div className="w-100 px-2 d-flex flex-column gap-2">
      {settings?.connectionType === "coexistence" && (
        <div className="small text-success text-center px-1">
          {intl.formatMessage({ id: "app.whatsappConnect.coexistenceActive" })}
        </div>
      )}
      <button
        className="btn btn-outline-primary w-100 mx-auto"
        color="primary"
        onClick={handleUnbind}
      >
        {intl.formatMessage({ id: "app.home.disconnect" })}
      </button>
    </div>
  );
}
